import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import CompanyProfile from './CompanyProfile';
import apiService from '../services/api';

const CompanyProfilePage = ({ selectedLocation }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCompany();
  }, [id]);

  const loadCompany = async () => {
    if (!id) {
      setError('No company ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const companyData = await apiService.getCRMCompany(id);
      setCompany(companyData);
    } catch (error) {
      console.error('Error loading company:', error);
      setError('Company not found');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/crm/companies');
  };

  const handleUpdate = (updatedCompany) => {
    setCompany(updatedCompany);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading company...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Company not found</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Company Profile</h1>
            <p className="text-sm text-gray-600">View and manage company information</p>
          </div>
        </div>
      </div>

      {/* Company Profile Content */}
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <CompanyProfile 
            company={company}
            onClose={handleBack}
            onUpdate={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyProfilePage;